import type { KeyDevice } from '../../types/vpnkey';
import { formatDateTimeShort } from '../../utils/formatDate';
import styles from './KeyDeviceBlock.module.css';

interface KeyDeviceBlockProps {
  device: KeyDevice | null;
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Значок платформы по строке из бэкенда; неизвестная — обобщённый «экран». */
function PlatformIcon({ platform }: { platform: string }) {
  const p = platform.trim().toLowerCase();

  if (p === 'ios' || p === 'ipados' || p === 'android') {
    // Смартфон
    return (
      <svg {...ICON_PROPS}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <line x1="10.5" y1="18.5" x2="13.5" y2="18.5" />
      </svg>
    );
  }
  if (p === 'macos' || p === 'mac' || p === 'linux' || p === 'windows') {
    // Ноутбук
    return (
      <svg {...ICON_PROPS}>
        <rect x="4" y="4" width="16" height="11" rx="1.5" />
        <path d="M2 19h20" />
        <path d="M9 19v-4M15 19v-4" />
      </svg>
    );
  }
  if (p === 'tvos' || p === 'tv' || p === 'androidtv') {
    // Телевизор
    return (
      <svg {...ICON_PROPS}>
        <rect x="2" y="4" width="20" height="13" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    );
  }
  // Неизвестно — просто экран
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M12 18v2" />
    </svg>
  );
}

/**
 * Блок «Устройство» на карточке ключа/триала: к чему привязан ключ,
 * когда последний раз был в сети. Отвязка — кнопкой внизу карточки
 * (ConnectButton в режиме connected).
 */
export function KeyDeviceBlock({ device }: KeyDeviceBlockProps) {
  if (!device) {
    return (
      <div className={styles.block}>
        <div className={styles.label}>Устройство</div>
        <p className={styles.empty}>Ключ ещё не вставлен в приложение. Нажмите «Подключить».</p>
      </div>
    );
  }

  const parts = [device.model || device.platform, device.app].filter(Boolean);
  const summary = parts.join(' · ') || device.title || 'Неизвестное устройство';
  const activity = device.lastActiveAt
    ? formatDateTimeShort(device.lastActiveAt)
    : 'ещё не подключался';

  return (
    <div className={styles.block}>
      <div className={styles.label}>Устройство</div>

      <div className={styles.summaryRow}>
        <span className={styles.icon} title={device.platform || undefined}>
          <PlatformIcon platform={device.platform} />
        </span>
        <span className={styles.summary}>{summary}</span>
      </div>

      {device.userAgent && <div className={styles.userAgent}>{device.userAgent}</div>}

      <div className={styles.activity}>
        Активность: <span className={styles.activityValue}>{activity}</span>
      </div>
    </div>
  );
}
